import { ref, computed, nextTick, type Ref } from 'vue'
import type { Item } from '@/shared/graphql/router/graphql'
import { isFile, isFolder } from '@/features/explorer/utils/itemGuards'

type ContextMenuType =
  | 'FileContextMenu'
  | 'MultipleFilesContextMenu'
  | 'FolderContextMenu'
  | 'MixedItemsContextMenu'

export function useExplorerContextMenu(options: {
  items: Ref<Item[]>
  selected: Ref<string[]>
  isProcessingFile: (item: Item) => boolean
}) {
  const { items, selected, isProcessingFile } = options

  const contextMenuComponent = ref<ContextMenuType | null>(null)
  const contextMenuRef = ref<HTMLElement | null>(null)
  const componentRef = ref<HTMLElement | null>(null)
  const contextMenuPos = ref({ x: 0, y: 0 })

  const contextMenuStyles = computed(() => ({
    top: `${contextMenuPos.value.y}px`,
    left: `${contextMenuPos.value.x}px`,
  }))

  const isFileContextMenu = computed(
    () => contextMenuComponent.value === 'FileContextMenu'
  )
  const isMultipleFilesContextMenu = computed(
    () => contextMenuComponent.value === 'MultipleFilesContextMenu'
  )
  const isFolderContextMenu = computed(
    () => contextMenuComponent.value === 'FolderContextMenu'
  )
  const isMixedItemsContextMenu = computed(
    () => contextMenuComponent.value === 'MixedItemsContextMenu'
  )

  const selectedItems = computed(() =>
    items.value.filter((item) => selected.value.includes(item.id))
  )
  const selectedFiles = computed(() => selectedItems.value.filter(isFile))

  const isRowSelected = (id: string) => selected.value.includes(id)

  const closeContextMenu = () => {
    contextMenuComponent.value = null
  }

  const handleWindowClick = () => {
    closeContextMenu()
    window.document.body.style.pointerEvents = 'initial'
    window.removeEventListener('click', handleWindowClick)
  }

  const resolveMenuType = (
    allFiles: boolean,
    allFolders: boolean,
    singleFile: boolean
  ): ContextMenuType => {
    if (singleFile && allFiles) return 'FileContextMenu'
    if (allFiles) return 'MultipleFilesContextMenu'
    if (allFolders) return 'FolderContextMenu'
    return 'MixedItemsContextMenu'
  }

  const positionMenu = async (clientX: number, clientY: number) => {
    await nextTick()

    const menuHeight = contextMenuRef.value?.clientHeight || 0
    const menuWidth = contextMenuRef.value?.clientWidth || 0

    const topOfMenu = clientY - menuHeight
    const bottomOfMenu = clientY + menuHeight

    const topValue =
      bottomOfMenu >= window.innerHeight
        ? topOfMenu <= 0
          ? 0 - topOfMenu
          : topOfMenu
        : clientY

    const rightBound =
      componentRef.value?.getBoundingClientRect().right ?? window.innerWidth

    if (clientX + menuWidth >= rightBound) {
      contextMenuPos.value.x = clientX - menuWidth
    } else {
      contextMenuPos.value.x = clientX
    }

    contextMenuPos.value.y = topValue
  }

  const handleRightClick = async (event: MouseEvent, row: Item) => {
    event.preventDefault()

    if (isProcessingFile(row)) return

    window.document.body.style.pointerEvents = 'none'
    window.addEventListener('click', handleWindowClick)

    const nextSelected =
      selected.value.length && isRowSelected(row.id) ? selected.value : [row.id]

    selected.value = nextSelected

    const selectedNow = items.value.filter((item) =>
      nextSelected.includes(item.id)
    )
    const allFiles = selectedNow.every(isFile)
    const allFolders = selectedNow.every(isFolder)
    const singleFile = nextSelected.length === 1

    contextMenuComponent.value = resolveMenuType(
      allFiles,
      allFolders,
      singleFile
    )

    await positionMenu(event.clientX, event.clientY)
  }

  return {
    contextMenuComponent,
    contextMenuRef,
    componentRef,
    contextMenuStyles,
    isFileContextMenu,
    isMultipleFilesContextMenu,
    isFolderContextMenu,
    isMixedItemsContextMenu,
    selectedItems,
    selectedFiles,
    closeContextMenu,
    handleRightClick,
  }
}
